import clsx from 'clsx';
import React, {
  KeyboardEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from '@emotion/styled';

import useTable from '../../hooks/useTable';
import EmptyState from '../Cards/EmptyState';
import RightTableSlider from './TableComponent/RightTableSlider';
import TableFilterAndSearchBox from './TableComponent/TableFilterAndSearchBox';
import { TableLoadingSkeleton } from './TableComponent/TableLoadingSkeleton';
import TableMoreOptions from './TableComponent/TableMoreOptions';
import TablePagination from './TableComponent/TablePagination';
import { TableContext, TableProvider } from './TableContext';
import { TableGroupContext } from './TableGroup';
import { TableGroupTitles } from './TableGroupTitles';
import { TableTitleWrapperContext } from './TableTitleWrapperContext';

interface TableAttr {
  type: 'regular' | 'filter' | 'custom-filter';
  comp: React.ReactNode;
}

export interface EmptyStateMsg {
  label: string;
  optionalComp?: React.ReactNode;
  icon?: React.ReactNode;
}

export interface TableFilterBoxProps {
  leftAttr?: TableAttr[];
  rightAttr?: TableAttr[];
  showSearchBox?: boolean;
  searchBoxPlaceholder?: string;
  customActionBtns?: React.ReactNode;
  filterBoxBottomBorder?: boolean;
}

interface TableProps {
  tableTitle?: string;
  showHeader?: boolean;
  dummyData?: Record<string, any>[];
  tableHeader?: Record<string, string>;
  moreOptionsComponents?: React.ReactNode[];
  emptyStateMsg?: EmptyStateMsg | string;
  emptyStateCustomComp?: React.ReactNode;
  cellsCustomComp?: Record<string, React.ReactNode>;
  hasClickableRow?: boolean;
  tableDataSourceUrl?: string;
  size?: number;
  defaultNumOfPagination?: number;
  clickRowAction?: (...args: any) => void;
  clickRowShowSliderComp?: (args: Record<string, any>) => React.ReactNode;
  border?: boolean;
  bgColor?: string;
  filterBoxBottomBorder?: boolean;
  tableFilterBoxFeatures?: TableFilterBoxProps;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
  mobileCellComp?: React.ReactNode;
  showPaginationControl?: boolean;
  className?: string;
  continuousView?: boolean;
  borderRadius?: string;
  headerBgColor?: string;
  firstColumnOffset?: string;
  height?: number;
  rmHeaderBorder?: boolean;
  addRowBorder?: boolean;
  stickyHeader?: boolean;
  tableColumnWidth?: Record<string, string>;
  standalone?: boolean;
  // Bump to re-fetch the current page in place, keeping search, filters,
  // page and any open slider (use instead of changing the Table's `key`).
  reloadSignal?: number;
}

const Table: React.FC<TableProps> = ({ showHeader, ...props }) => {
  return (
    <TableProvider>
      <TableRaw {...props} />
    </TableProvider>
  );
};

interface TableRawProps extends TableProps {}

const TableRaw: React.FC<TableRawProps> = ({
  tableHeader = { 'Hethera Table': 'HetheraTable' },
  moreOptionsComponents,
  emptyStateMsg,
  emptyStateCustomComp,
  cellsCustomComp,
  hasClickableRow,
  tableDataSourceUrl,
  dummyData,
  size = 10,
  defaultNumOfPagination,
  clickRowAction,
  clickRowShowSliderComp,
  border,
  bgColor,
  filterBoxBottomBorder,
  tableFilterBoxFeatures,
  mobileCellComp,
  mobileOnlyView = false,
  showPaginationControl = true,
  tableTitle,
  className,
  continuousView,
  borderRadius,
  headerBgColor,
  firstColumnOffset,
  height,
  rmHeaderBorder = false,
  addRowBorder = false,
  stickyHeader = false,
  disableMobileComp = false,
  tableColumnWidth,
  standalone = false,
  reloadSignal,
}) => {
  const tableContext = useContext(TableContext);
  const tableGroupContext = useContext(TableGroupContext);
  const tableTitleWrapperContext = useContext(TableTitleWrapperContext);
  const [page, setPage] = useState<number>(1);
  const [hasStartedInitialFetch, setHasStartedInitialFetch] =
    useState<boolean>(false);
  const hasStaticData = Array.isArray(dummyData);

  const {
    endPage,
    tableData,
    loading: tableLoading,
    // setLoading,
    fetchTableData,
    loadedOnce,
  } = useTable();

  const tableColumns = useMemo(() => Object.keys(tableHeader), [tableHeader]);
  const tableColumnKeys = useMemo(
    () => Object.values(tableHeader),
    [tableHeader],
  );

  const gridTemplateColumns = useMemo(() => {
    if (!tableColumnWidth) return undefined;
    return tableColumns.map((col) => tableColumnWidth[col] ?? '1fr').join(' ');
  }, [tableColumns, tableColumnWidth]);

  const resolvedEmptyState = useMemo<EmptyStateMsg>(() => {
    if (typeof emptyStateMsg === 'string') {
      return {
        label: emptyStateMsg,
        optionalComp: emptyStateCustomComp,
      };
    }

    if (emptyStateMsg) {
      return {
        ...emptyStateMsg,
        optionalComp:
          emptyStateMsg.optionalComp === undefined
            ? emptyStateCustomComp
            : emptyStateMsg.optionalComp,
      };
    }

    return {
      label: 'Table is empty',
      optionalComp: emptyStateCustomComp,
    };
  }, [emptyStateCustomComp, emptyStateMsg]);

  const resolvedTableData = useMemo<Record<string, any>[]>(() => {
    if (Array.isArray(dummyData)) {
      return dummyData;
    }

    if (Array.isArray(tableData)) {
      return tableData;
    }

    return [];
  }, [dummyData, tableData]);

  useEffect(() => {
    if (!hasStaticData && tableContext?.tableFilterUrl) {
      setHasStartedInitialFetch(true);
      fetchTableData(tableContext?.tableFilterUrl);
    }
  }, [
    fetchTableData,
    hasStaticData,
    tableContext?.tableFilterUrl,
    tableContext?.refreshTable,
  ]);

  useEffect(() => {
    if (hasStaticData) {
      setHasStartedInitialFetch(true);
    }
  }, [hasStaticData]);

  const firstReloadSignal = useRef(reloadSignal);
  useEffect(() => {
    if (reloadSignal === firstReloadSignal.current) return;
    if (!hasStaticData && tableContext?.tableFilterUrl) {
      fetchTableData(tableContext.tableFilterUrl);
    }
    // Only a new signal triggers this; filter changes fetch above.
    // eslint-disable-next-line
  }, [reloadSignal]);

  useEffect(() => {
    tableContext?.setTableBaseUrl(tableDataSourceUrl);
    if (page && size) {
      tableContext?.handleSetTableFilterRecord({
        page: page - 1,
        size,
      });
    } else {
      tableContext?.setTableFilterUrl(tableDataSourceUrl);
    }
    // eslint-disable-next-line
  }, [page, size, tableDataSourceUrl]);

  useEffect(() => {
    tableTitleWrapperContext?.setViewShowMore(resolvedTableData.length > 0);
  }, [resolvedTableData.length, tableTitleWrapperContext]);

  const handleClickRowShowSlider = useCallback(
    (comp: React.ReactNode) => {
      tableContext?.setShowTableSlider(true);
      tableContext?.setSliderComp(comp);
    },
    [tableContext],
  );

  const handleRowClick = useCallback(
    (row: Record<string, any>) => {
      clickRowAction && clickRowAction(row);

      if (clickRowShowSliderComp) {
        handleClickRowShowSlider(clickRowShowSliderComp(row));
      }
    },
    [clickRowAction, clickRowShowSliderComp, handleClickRowShowSlider],
  );

  const handleRowKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>, row: Record<string, any>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowClick(row);
      }
    },
    [handleRowClick],
  );

  const isRowClickable = Boolean(
    hasClickableRow || clickRowAction || clickRowShowSliderComp,
  );

  // Skeleton only until the first answer; after that a filter, search or page
  // change keeps the rows in place, dimmed, under a thin progress line.
  const showLoadingState =
    !hasStaticData && (!hasStartedInitialFetch || !loadedOnce);
  const isRefreshing = !hasStaticData && loadedOnce && tableLoading;

  return (
    <Container
      border={border}
      borderRadius={borderRadius}
      className={clsx(
        className,
        height && 'has-height',
        showLoadingState && 'is-loading',
        isRefreshing && 'is-refreshing',
      )}
      aria-busy={showLoadingState || isRefreshing}
    >
      {tableFilterBoxFeatures && (
        <div className='table-toolbar'>
          <TableFilterAndSearchBox
            {...tableFilterBoxFeatures}
            filterBoxBottomBorder={filterBoxBottomBorder}
          />
        </div>
      )}
      {/* Table segment control */}
      {tableGroupContext && !standalone && <TableGroupTitles />}

      <TableOfContent
        className={clsx(
          className,
          height && 'y-scrollable-table',
          showLoadingState && 'is-loading',
        )}
        bgColor={bgColor}
        showPaginationControl={showPaginationControl}
        height={height}
        columns={tableColumns.length}
        gridTemplateColumns={gridTemplateColumns}
        continuousView={continuousView}
        disableMobileComp={disableMobileComp}
        mobileOnlyView={mobileOnlyView}
      >
        {tableTitle && (
          <TableLabelBox>
            <TableLabel>{tableTitle}</TableLabel>
          </TableLabelBox>
        )}

        {/* Table Header section */}
        <TableOfContentHeader
          border={border}
          bgColor={headerBgColor ?? '#FAF9F5'}
          firstColumnOffset={firstColumnOffset ?? (border ? '24px' : '12px')}
          rmHeaderBorder={rmHeaderBorder}
          className={clsx(
            className,
            tableFilterBoxFeatures ? 'zero-top-border' : '',
            stickyHeader ? 'sticky-header' : '',
          )}
          continuousView={continuousView}
          disableMobileComp={disableMobileComp}
          mobileOnlyView={mobileOnlyView}
        >
          {tableColumns.map((col) => (
            <div key={col}>{col}</div>
          ))}
        </TableOfContentHeader>

        {/* Table rows section */}
        {showLoadingState ? (
          <TableStatusContainer aria-live='polite' aria-busy='true'>
            <TableLoadingSkeleton />
          </TableStatusContainer>
        ) : resolvedTableData.length === 0 ? (
          <TableStatusContainer>
            <EmptyState
              label={resolvedEmptyState.label}
              optionalComp={resolvedEmptyState.optionalComp}
              icon={resolvedEmptyState.icon}
            />
          </TableStatusContainer>
        ) : (
          <>
            {resolvedTableData.map((row, index) => {
              const rowKey = String(row?.id ?? row?._id ?? index);
              return (
                <TableRowUnitData
                  key={rowKey}
                  className={clsx(className, 'table-row')}
                  hasClickableRow={isRowClickable}
                  continuousView={continuousView}
                  disableMobileComp={disableMobileComp}
                  mobileOnlyView={mobileOnlyView}
                  addRowBorder={addRowBorder || !rmHeaderBorder}
                  isLastRow={index === resolvedTableData.length - 1}
                  firstColumnOffset={firstColumnOffset ?? (border ? '24px' : '12px')}
                  onClick={
                    isRowClickable ? () => handleRowClick(row) : undefined
                  }
                  onKeyDown={
                    isRowClickable
                      ? (event) => handleRowKeyDown(event, row)
                      : undefined
                  }
                  role={isRowClickable ? 'button' : undefined}
                  tabIndex={isRowClickable ? 0 : -1}
                >
                  {/* Table row  */}
                  {tableColumnKeys.map((col) => {
                    const customCell = cellsCustomComp?.[col];

                    if (customCell) {
                      return React.Children.map(
                        customCell,
                        (child: any, childIndex) =>
                          React.cloneElement(child, {
                            ...child.props,
                            key: `${rowKey}-${col}-${childIndex}`,
                            data: row,
                          }),
                      );
                    }

                    let computedRowData = row[col];
                    if (
                      computedRowData === undefined ||
                      computedRowData === null ||
                      computedRowData === ''
                    ) {
                      computedRowData = (
                        <EmptyCell className={clsx(className)}>N/A</EmptyCell>
                      );
                    }

                    // Plain values stay on one line; the full text is in the
                    // tooltip (and the row's detail panel).
                    const isText =
                      typeof computedRowData === 'string' ||
                      typeof computedRowData === 'number';
                    return (
                      <div
                        key={`${rowKey}-${col}`}
                        className={isText ? 'cell-text' : undefined}
                        title={isText ? String(computedRowData) : undefined}
                      >
                        {computedRowData}
                      </div>
                    );
                  })}

                  {/* Table row more option comp */}
                  {moreOptionsComponents && (
                    <TableMoreOptions
                      moreOptionsComponents={moreOptionsComponents}
                      data={row}
                    ></TableMoreOptions>
                  )}
                </TableRowUnitData>
              );
            })}

            {/* Mobile responsiveness */}
            {((!disableMobileComp && !continuousView) || mobileOnlyView) && (
              <MobileCellContainer mobileOnlyView={mobileOnlyView}>
                {resolvedTableData.map((row, index) => {
                  return (
                    mobileCellComp &&
                    React.Children.map(mobileCellComp, (child: any) => {
                      return React.cloneElement(child, {
                        ...child.props,
                        key: row?.id ?? row?._id ?? index,
                        data: row,
                        moreOptionsComponents: moreOptionsComponents,
                      });
                    })
                  );
                })}
              </MobileCellContainer>
            )}
          </>
        )}
      </TableOfContent>
      {/* Right slider Component */}
      <RightTableSlider />
      {endPage !== 0 && showPaginationControl && (
        <div className='table-footer'>
          <TablePagination
            className={className}
            endPage={endPage}
            defaultNumOfPagination={defaultNumOfPagination}
            changePage={setPage}
          />
        </div>
      )}
    </Container>
  );
};

export default Table;

interface ContainerProps {
  border?: boolean;
  borderRadius?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  border-radius: ${({ borderRadius }) => borderRadius || '14px'};
  overflow: hidden;

  /* The page table: one white card, like the website's cards. Toolbar
     strip on top, tinted header row, pagination strip at the bottom. */
  ${({ border }) =>
    border &&
    `
    background: #ffffff;
    border: 1px solid #E2DED3;
    box-shadow: var(--shadow-xs);

    & > .table-toolbar {
      padding: 14px 16px;
      border-bottom: 1px solid #E2DED3;
    }
    & > .table-footer {
      padding: 10px 16px;
      border-top: 1px solid #E2DED3;
      background: var(--panel);
    }
    & > .table-footer > div {
      margin-top: 0;
    }

    @media (max-width: 884px) {
      background: transparent;
      border: 0;
      box-shadow: none;
      border-radius: 0;

      & > .table-toolbar {
        padding: 0 0 16px;
        border-bottom: 0;
      }
      & > .table-footer {
        padding: 12px 0 0;
        border-top: 0;
      }
    }
  `}

  /* Inside another card: room under the toolbar, and a 2px inset so field
     borders and focus rings aren't clipped by the rounded corners. */
  ${({ border }) =>
    !border &&
    `
    & > .table-toolbar {
      padding: 2px 2px 16px;
    }
  `}

  & > .table-toolbar > div {
    margin-bottom: 0;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
  }

  &.scrollable-table {
    width: 100%;
    min-width: max-content;
    position: relative;
  }

  &.scrollable-table.is-loading {
    min-width: unset;
  }

  /* Refreshing in place: rows stay, dimmed, with a thin bar sliding across
     the top. No skeleton, so the layout doesn't jump. */
  &.is-refreshing::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    z-index: 2;
    height: 2px;
    width: 35%;
    background: #1f7a45;
    border-radius: 2px;
    animation: tableRefresh 0.9s ease-in-out infinite;
  }
  &.is-refreshing .table-row {
    opacity: 0.55;
    transition: opacity 0.15s ease;
  }

  @keyframes tableRefresh {
    from {
      transform: translateX(-100%);
    }
    to {
      transform: translateX(290%);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    &.is-refreshing::after {
      animation: none;
      width: 100%;
      opacity: 0.4;
    }
  }
`;

interface TableOfContentProps {
  columns?: number;
  gridTemplateColumns?: string;
  border?: boolean;
  bgColor?: string;
  showPaginationControl?: boolean;
  continuousView?: boolean;
  height?: number;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}
const TableOfContent = styled.div<TableOfContentProps>`
  display: grid;
  grid-template-columns: ${({ columns, gridTemplateColumns }) =>
    gridTemplateColumns ?? `repeat(${columns}, 1fr)`};
  align-content: start;
  width: 100%;
  background-color: ${({ bgColor }) => (!bgColor ? `white` : bgColor)};
  word-break: break-word;

  &.y-scrollable-table {
    height: ${({ height }) => (height ? `${height}px` : '100%')};
    overflow-y: auto;
  }
  &.sticky-header {
    position: relative;
  }

  &.scrollable-table {
    min-width: max-content;
  }

  &.scrollable-table.is-loading {
    min-width: unset;
  }

  border-bottom: ${({ showPaginationControl, theme }) =>
    !showPaginationControl
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};

  & > * {
    grid-column: ${({ columns }) => `1/${(columns ?? 0) + 1}`};
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView || (!continuousView && !disableMobileComp)
        ? 'flex'
        : 'grid'};
    flex-direction: ${({ continuousView, mobileOnlyView }) =>
      mobileOnlyView || !continuousView ? 'column' : 'unset'};
    background-color: transparent;
  }
`;

export const TableLabelSection = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

// Section/card titles: small and confident (Stripe), not display-sized.
export const TableLabel = styled.div`
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: #0E1A13;
  width: fit-content;
`;

export const TableLabelBox = styled.div`
  display: flex;
  margin-bottom: 16px;
`;

interface TableOfContentHeaderProps {
  border?: boolean;
  rmHeaderBorder?: boolean;
  continuousView?: boolean;
  bgColor?: string;
  firstColumnOffset?: string;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}

export const TableOfContentHeader = styled.div<TableOfContentHeaderProps>`
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'none' : 'grid')};
  grid-template-columns: inherit;
  border-bottom: ${({ rmHeaderBorder, theme }) =>
    rmHeaderBorder
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};
  border-top: ${({ border, theme }) =>
    border
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};
  word-break: break-word;
  padding-left: 0px;
  background-color: ${({ bgColor }) => (!bgColor ? `none` : bgColor)};
  height: fit-content;
  ${({ border }) =>
    !border &&
    `
    border-radius: 10px;
    border-top-color: transparent;
    border-bottom-color: transparent;
  `}

  &.sticky-header {
    /* border: 1px solid red; */
    position: sticky;
    top: 0px;
    z-index: 1;
  }

  &.scrollable-table {
    border: 1px solid transparent;
    border-bottom: 1px solid ${({ theme }) => theme.palette.borderColor};
  }

  /* Column labels: small, sentence case, muted (Stripe/Vercel). */
  & > div {
    padding: 10px 16px;
    white-space: nowrap;

    &:last-of-type {
      padding-right: 24px;
    }

    font-size: 12.5px;
    font-weight: 500;
    color: #5D665F;

    &:first-of-type {
      padding-left: ${({ firstColumnOffset }) => firstColumnOffset ?? '0px'};
    }
  }

  &.scrollable-table > div {
    padding-left: 14px;
    color: #4F5A52;
    /* border: 1px solid red; */
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView
        ? 'none'
        : continuousView || disableMobileComp
          ? 'grid'
          : 'none'};
  }
`;

interface TableRowUnitDataProp {
  hasClickableRow?: boolean;
  children: ReactNode;
  continuousView?: boolean;
  firstColumnOffset?: string;
  addRowBorder?: boolean;
  isLastRow?: boolean;
  disableMobileComp?: boolean;
  mobileOnlyView?: boolean;
}

export const TableRowUnitData = styled.div<TableRowUnitDataProp>`
  position: relative;
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'none' : 'grid')};
  grid-template-columns: inherit;
  align-items: center;
  cursor: ${({ hasClickableRow }) => (hasClickableRow ? 'pointer' : 'default')};
  border-bottom: ${({ addRowBorder, isLastRow, theme }) =>
    !addRowBorder || isLastRow
      ? '1px solid transparent'
      : `1px solid ${theme.palette.borderColor}`};

  height: fit-content;

  & > div {
    min-width: 0;
    padding: 13px 16px;
    font-size: 14px;
    color: ${({ theme }) => theme.palette.tertiaryTextColor};

    &:last-of-type {
      padding-right: 24px;
    }

    &.cell-text {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &:first-of-type {
      padding-left: ${({ firstColumnOffset }) => firstColumnOffset ?? '0px'};
    }
  }

  &.scrollable-table > div {
    padding-left: 14px;
    color: #5D665F;
  }

  &:hover {
    background-color: #FAF9F5;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.HetheraPrimary};
    outline-offset: -2px;
  }

  @media (max-width: 884px) {
    display: ${({ continuousView, disableMobileComp, mobileOnlyView }) =>
      mobileOnlyView
        ? 'none'
        : continuousView || disableMobileComp
          ? 'grid'
          : 'none'};
  }
`;

export const TableLabelCustomComp = styled.div`
  display: flex;
  gap: 16px;
`;

const EmptyCell = styled.div`
  color: ${({ theme }) => theme.palette.tertiaryTextColor};
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  /* border: 1px solid red; */

  &.scrollable-table {
    color: #5D665F;
  }
`;

const MobileCellContainer = styled.div<{ mobileOnlyView?: boolean }>`
  flex-direction: column;
  display: ${({ mobileOnlyView }) => (mobileOnlyView ? 'flex' : 'none')};

  @media (max-width: 884px) {
    display: flex;

    & > * {
      background-color: white;
    }
  }
`;

const TableStatusContainer = styled.div`
  padding: 38px;
  min-width: 0;
  overflow: hidden;
`;
